import React, { useCallback, useMemo, useRef, useState } from "react";
import * as S from "./HologramTour.styles";
import { useResizeDetector } from "react-resize-detector";
import {
  ANIMATIONS_BY_TAB_TYPE,
  PRODUCT_TAB_GROUP,
} from "./HologramTour.config";
import { StyledContainer } from "components/StyledContainer";
import { MicrositeHeading } from "pages/LayoutComponents/MicrositeHeading";
import { useTranslation } from "hooks/useTranslation";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ProgressAnimationButton } from "components/ProgressAnimationButton";
import { TabType } from "./HologramTour.types";
import { TourContent } from "./TourContent";

const INITIAL_WIDTH = 1440;

export const HologramTour = ({
  isInView,
}: Readonly<{ isInView?: boolean }>) => {
  const { t } = useTranslation();
  const { width, ref } = useResizeDetector();

  const container = useRef<HTMLDivElement>(null);
  const tourRef = useRef<HTMLDivElement>(null);

  const [selectedApplication, setSelectedApplication] = useState(
    TabType.Network,
  );

  const [hasLoadedAsset, setHasLoadedAsset] = useState(false);

  useGSAP(
    () => {
      const timeline = gsap.timeline();

      timeline
        .fromTo(
          tourRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.75, ease: "power1.inOut" },
        )
        .fromTo(
          ".tour-header",
          { opacity: 0, y: "15%" },
          {
            opacity: 1,
            y: 0,
            delay: -0.6,
            duration: 0.75,
            ease: "power1.inOut",
          },
        );
    },
    { scope: container },
  );

  const tabs = useMemo(() => {
    return PRODUCT_TAB_GROUP.map((tab) => ({ ...tab, label: t(tab.label) }));
  }, [t]);

  const imageScale = useMemo(() => {
    if (width >= INITIAL_WIDTH) {
      return 1;
    }

    const ratio = width / INITIAL_WIDTH;

    return Number.isNaN(Number(ratio)) ? 1 : ratio;
  }, [width]);

  const currentVideoIndex = useMemo(() => {
    return Object.keys(ANIMATIONS_BY_TAB_TYPE).findIndex(
      (tab) => tab === selectedApplication,
    );
  }, [selectedApplication]);

  const getNextIndex = () => {
    const keys = Object.keys(ANIMATIONS_BY_TAB_TYPE);
    const endIndex = keys.length - 1;

    return currentVideoIndex === endIndex ? 0 : currentVideoIndex + 1;
  };

  const animationData = useMemo(() => {
    return Object.entries(ANIMATIONS_BY_TAB_TYPE).map(([id, animation]) => ({
      id,
      animation,
    }));
  }, []);

  const activeData = useMemo(() => {
    return animationData.find(({ id }) => id === selectedApplication);
  }, [animationData, selectedApplication]);

  const isActiveTab = useCallback(
    (tabId: string) => {
      if (!isInView) {
        return false;
      }

      return hasLoadedAsset && selectedApplication === tabId;
    },
    [isInView, hasLoadedAsset, selectedApplication],
  );

  const handleReadyForPlayback = useCallback(() => {
    setHasLoadedAsset(true);
  }, []);

  return (
    <S.TourSectionWrapper ref={container}>
      <S.TitleSwitchContainer className="tour-header">
        <StyledContainer
          size="desktop"
          noVerticalMargin
          $spacing={{ top: 0, bottom: 0 }}
        >
          <MicrositeHeading
            title={t("landing.tour.title")}
            renderAs="h1"
            isCentered
            isMainTitle
            spacing={{ top: 32, bottom: 0 }}
          />
        </StyledContainer>

        <S.StyledScrollWrapper
          direction="horizontal"
          align="center"
          scrollStepType="itemWidth"
          hideArrows
          enableWheelScroll
          gap={12}
          activeIndex={tabs.findIndex(({ id }) => id === selectedApplication)}
          items={tabs.map((tab) => ({
            id: String(tab.id),
            key: tab.id,
            render: () => (
              <ProgressAnimationButton
                icon={tab.icon}
                text={tab.label}
                isActive={selectedApplication === tab.id}
                canPlayAnimation={isActiveTab(tab.id)}
                variant="secondary"
                playbackTimeInSeconds={tab.playbackTimeInSeconds}
                handleClick={() => setSelectedApplication(tab.id)}
              />
            ),
          }))}
        />
      </S.TitleSwitchContainer>

      <div ref={tourRef}>
        <S.StyledTourWrapper ref={ref}>
          <TourContent
            animation={activeData.animation}
            scale={imageScale}
            isInView={isInView}
            onCanPlayVideo={handleReadyForPlayback}
            onPlaybackEnd={() => {
              const nextIndex = getNextIndex();

              setSelectedApplication(
                PRODUCT_TAB_GROUP[nextIndex].id as TabType,
              );
            }}
          />
        </S.StyledTourWrapper>
      </div>
    </S.TourSectionWrapper>
  );
};
